import { useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Adg4Button } from "@/components/adg4/button/src";
import { wacToken } from "@/libraries/design/foundations";
import { USER_SEARCH_PARAMS } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts";

import { useGupsStatus } from "../../../hooks";
import { useScreenManager } from "../../../hooks/use-screen-manager";
import { useJfindSimplifiedListViewWithBasicTasksUpdateTargetingGateEnabled } from "../../../hooks/use-simplified-list-view-targeting-gate-enabled";
import {
    getCurrentUrlWithParams,
    useEligibleTeamTypeForJtbdLocalStorage,
    mapJTBDtoTemplateChoice
} from "../../../utils/utils";
import { checkboxValues } from "../constants";
import { messages } from "../messages";

const buttonWrapperStyle = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    margin: "0 auto",
    padding: "36px 20px 20px 20px",
    border: "none",
    gap: token("space.250"),
    width: "372px",
    [media.below.sm]: {
        position: "fixed",
        insetInlineStart: 0,
        insetBlockEnd: "0",
        backgroundColor: wacToken("color.neutral.light0"),
        padding: "14px 18px",
        width: "100%",
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "-1px 0px 2px 0px #00000040"
    }
});
const Buttons = ({
    checkboxes,
    teamType
}: {
    checkboxes: checkboxValues[];
    teamType?: string;
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { prevScreen, nextScreen } = useScreenManager();
    const intl = useIntl();
    const { addJtbdCallback } = useGupsStatus();
    const isGateEnabled =
        useJfindSimplifiedListViewWithBasicTasksUpdateTargetingGateEnabled();
    const removeUrlParameter = useCallback((url: string, paramKey: string) => {
        const r = new URL(url);
        r.searchParams.delete(paramKey);
        return r.href;
    }, []);
    // Don't render the back button if team is set in localStorage
    const teamSetFromLocalStorage = useEligibleTeamTypeForJtbdLocalStorage();
    const BackButton = (
        <Adg4Button
            variant="secondary"
            size="small"
            color="black"
            onClick={() => {
                const updatedUrl = removeUrlParameter(
                    window.location.href,
                    USER_SEARCH_PARAMS.teamType
                );
                history.replaceState({}, "", updatedUrl);
                prevScreen();
            }}
        >
            {intl.formatMessage({
                id: messages.backButton.id,
                defaultMessage: messages.backButton.defaultMessage
            })}
        </Adg4Button>
    );
    return (
        <div css={buttonWrapperStyle}>
            {!teamSetFromLocalStorage ? BackButton : ""}
            <Adg4Button
                variant="primary"
                size="small"
                color="blue"
                onClick={async () => {
                    const checkedCheckboxVals = checkboxes.filter(
                        (checkbox) => checkbox.isChecked
                    );
                    const checkedBoxes = checkedCheckboxVals.map(
                        (checkbox) => checkbox.index
                    );
                    const { template, persona } = mapJTBDtoTemplateChoice(
                        checkboxes,
                        teamType,
                        isGateEnabled
                    );
                    const jtbd =
                        checkedBoxes.length > 0 ? checkedBoxes.join(",") : "0";
                    const jtbdVals =
                        checkedCheckboxVals.length > 0
                            ? checkedCheckboxVals
                                  .map((checkbox) => checkbox.value)
                                  .join(",")
                            : "NONE";
                    createAnalyticsEvent({
                        actionSubject: "button",
                        action: "clicked",
                        actionSubjectId: "jtbdContinue",
                        attributes: {
                            jtbd,
                            jtbdVals,
                            templateId: template,
                            teamType
                        }
                    }).fire();

                    await addJtbdCallback(checkboxes, template);

                    const updatedUrl = getCurrentUrlWithParams({
                        jtbdTemplateOverride: template ?? "",
                        jtbd:
                            checkedBoxes.length > 0 ? checkedBoxes.join() : "0",
                        jtbdPersona: persona ?? ""
                    });
                    history.replaceState({}, "", updatedUrl);
                    nextScreen();
                }}
            >
                {intl.formatMessage({
                    id: messages.continueButton.id,
                    defaultMessage: messages.continueButton.defaultMessage
                })}
            </Adg4Button>
        </div>
    );
};
export default Buttons;
